import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import UsersApi from "Classes/API/UsersApi";
import TenantManager from "Classes/API/TenantManager";
import TenantsApi from "Classes/API/Administration/TenantsApi";
import FeatureManagementApi from "../Classes/API/FeatureManagementApi";
import { jwtDecode } from "jwt-decode";
import { UserContext } from "../App";
import { useAuthState } from "react-firebase-hooks/auth";
import { getAuth } from "firebase/auth";

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = getAuth();
  const user = useContext(UserContext);

  const fetchFeatures = useCallback(async () => {
    try {
      const result = await FeatureManagementApi.GetAllFeatures();
      if (result && result.status === "Success") {
        setFeatures(result.data);
      } else {
        throw new Error("Failed to retrieve features from the server.");
      }
    } catch (err) {
      console.error("Error fetching features:", err);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong while loading features!",
        footer: "Please try again later or contact support.",
      });
    }
  }, []);

  const updateTenantFeatures = async (enabledIds) => {
    try {
      const result = await FeatureManagementApi.UpdateFeatures(enabledIds);

      if (result && result.status === "Success") {
        await fetchFeatures();
        return true;
      } else {
        Swal.fire({
          icon: "warning",
          title: "Update Failed",
          text:
            result?.message || "Could not update features. Please try again.",
        });
        return false;
      }
    } catch (err) {
      console.error("Error updating features:", err);

      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: "An unexpected error occurred while saving changes.",
      });

      throw err;
    }
  };

  // Fetch all tenants
  const fetchTenants = async () => {
    try {
      setLoading(true);
      const result = await TenantsApi.GetTenants();

      if (Array.isArray(result.data)) {
        const formattedTenants = result.data.map((tenant) => ({
          id: tenant.tenantId,
          name: tenant.name,
        }));
        setTenants(formattedTenants);
        return formattedTenants;
      } else {
        throw new Error("API did not return an array");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching tenants:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Extract tenantId ONCE when user changes
  useEffect(() => {
    const extractTenantId = async () => {
      try {
        if (!user) {
          setTenantId(null);
          return;
        }

        const token = await UsersApi.GetToken();
        if (!token || typeof token !== "string") {
          console.error("Invalid token received:", token);
          setTenantId(null);
          return;
        }

        const tenantId = TenantManager.getTenant(token);
        console.log("Determined Tenant ID:", tenantId);
        setTenantId(tenantId);
      } catch (err) {
        setError(err.message);
        console.error("Error extracting tenant ID:", err);
        setTenantId(null);
      }
    };

    extractTenantId();
  }, [user]);

  useEffect(() => {
    if (tenantId) {
      fetchFeatures();
    }
  }, [tenantId, fetchFeatures]);

  const currentTenant = useMemo(() => {
    if (!tenantId) return null;
    return tenants.find((t) => t.id === tenantId);
  }, [tenantId, tenants]);

  // Switch tenant
  const switchTenant = async (tenantId) => {
    try {
      TenantManager.setTenant(tenantId);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      window.location.reload();
    } catch (err) {
      setError(err.message);
      console.error("Error switching tenant:", err);
    }
  };

  const isSuperAdmin = useMemo(() => {
    if (!user || !user.accessToken) return false;
    try {
      const decoded = jwtDecode(user.accessToken);
      return decoded?.tenant === "superadmin";
    } catch (err) {
      console.error("Error decoding access token:", err);
      return false;
    }
  }, [user]);

  // Initial load when user changes
  useEffect(() => {
    if (user) {
      fetchTenants();
    } else {
      setTenants([]);
      setTenantId(null);
      setLoading(false);
    }
  }, [user]);

  const value = {
    tenants,
    currentTenant,
    features,
    updateTenantFeatures,
    loading,
    error,
    isSuperAdmin,
    switchTenant,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};
